import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { Link } from '@inertiajs/react'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { fadeInUp, staggerContainer } from '@/lib/motion'

// Tier names, prices, and feature lines preserved from the original
// PricingSection so the marketing copy continues to reflect what's
// actually shipped (or not). New invented claims (SLA percentages,
// SCIM, HIPAA BAA, SOC2 evidence) have been removed.
const tiers = [
  {
    name: 'Free',
    price: '$0',
    unit: '/month',
    description: 'Perfect for prototyping and side projects.',
    cta: 'Get Started',
    highlighted: false,
    features: [
      '1,000 participant-minutes/mo',
      '1 application',
      '10 concurrent participants',
      'Community support',
      'REST API access',
    ],
  },
  {
    name: 'Growth',
    price: '$49',
    unit: '/month',
    description: 'For teams shipping realtime features to production.',
    cta: 'Start Free Trial',
    highlighted: true,
    features: [
      '50,000 participant-minutes/mo',
      '10 applications',
      '100 concurrent participants',
      'Priority email support',
      'Analytics dashboard',
      'Custom TURN servers',
    ],
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    unit: '',
    description: 'For organizations with advanced needs.',
    cta: 'Contact Sales',
    highlighted: false,
    features: [
      'Unlimited participant-minutes',
      'Unlimited applications',
      'Unlimited concurrent participants',
      'Dedicated support engineer',
      'SLA guarantee',
      'Custom deployment',
      'SSO & audit logs',
    ],
  },
]

export function PricingSection() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section
      id="pricing"
      aria-label="Pricing"
      className="relative py-28 lg:py-40"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mb-16 max-w-2xl"
        >
          <motion.div variants={fadeInUp} className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            05 / pricing
          </motion.div>
          <motion.h2
            variants={fadeInUp}
            className="mt-3 font-heading text-3xl font-bold leading-tight tracking-tight sm:text-4xl"
          >
            Simple, Transparent Pricing
          </motion.h2>
          <motion.p variants={fadeInUp} className="mt-5 text-lg text-muted-foreground">
            Start free, scale as you grow. No hidden fees, no surprises.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid gap-5 md:grid-cols-3"
        >
          {tiers.map((tier) => (
            <motion.div
              key={tier.name}
              variants={fadeInUp}
              className={cn(
                'surface-card relative flex h-full flex-col overflow-hidden p-7',
                tier.highlighted && 'signal-border',
              )}
            >
              {tier.highlighted && (
                <>
                  <div
                    aria-hidden
                    className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full"
                    style={{
                      background:
                        'radial-gradient(circle, hsl(var(--brand-2) / 0.25) 0%, transparent 70%)',
                    }}
                  />
                  <span className="absolute right-5 top-5 rounded-md bg-foreground px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-background">
                    Most Popular
                  </span>
                </>
              )}

              <div className="relative">
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  {tier.name}
                </div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-heading text-6xl leading-none tracking-tight sm:text-7xl">
                    {tier.price}
                  </span>
                  {tier.unit && (
                    <span className="ml-1 font-mono text-sm text-muted-foreground">
                      {tier.unit}
                    </span>
                  )}
                </div>
                <p className="mt-3 text-sm text-muted-foreground">{tier.description}</p>

                <Button
                  asChild
                  variant={tier.highlighted ? 'default' : 'outline'}
                  className="mt-7 w-full rounded-lg"
                >
                  <Link href={tier.name === 'Enterprise' ? '/contact' : '/sign_up'}>
                    {tier.cta}
                  </Link>
                </Button>

                <ul className="mt-7 space-y-3">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm">
                      <Check
                        className={cn(
                          'mt-0.5 h-4 w-4 shrink-0',
                          tier.highlighted ? 'text-[hsl(var(--signal-live))]' : 'text-foreground/70',
                        )}
                      />
                      <span className="text-foreground/85">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Savings strip */}
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="surface-card mt-10 flex flex-col items-start gap-5 p-7 sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Reference quote · 1M participant-minutes / month
            </div>
            <div className="mt-2 flex flex-wrap items-baseline gap-3">
              <span className="font-heading text-3xl tracking-tight">$1,000</span>
              <span className="text-sm text-muted-foreground line-through">$3,990 on Agora</span>
              <span className="rounded-md bg-[hsl(var(--signal-live)/0.15)] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--signal-live))]">
                ≈ 75% less
              </span>
            </div>
            <p className="mt-2 max-w-md text-xs text-muted-foreground">
              Calculated from $1.00 vs $3.99 per 1k participant-minutes — the same per-minute rates used in the dashboard usage view.
            </p>
          </div>
          <Link
            href="/contact"
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-foreground hover:text-[hsl(var(--brand-1))]"
          >
            Get a custom quote ↗
          </Link>
        </motion.div>
      </div>
    </section>
  )
}
